import "./ShowForm.css";

import { Breadcrumb, Button, Divider, message, Tag } from "antd";
import { BiEdit } from "react-icons/bi";
import { BsTrash } from "react-icons/bs";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  deleteForm,
  type FieldType,
  type FormBodyType,
  useAppSelector,
} from "../../../_lib/_store";

export const ShowFormPage = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { isLoading } = useAppSelector((state) => state.form);

  const navigate = useNavigate();
  const { state } = useLocation();

  const formObj = state?.form;

  const handleDelete = async () => {
    const res = await deleteForm(messageApi, formObj?.id);
    if (res) {
      navigate("/form");
    }
  };

  const renderFormPreview = (formBody: FormBodyType) => {
    if (!formBody) {
      return <p>No form body data available</p>;
    }

    if (
      !formBody.fields ||
      !Array.isArray(formBody.fields) ||
      formBody.fields.length === 0
    ) {
      return <p>No form fields defined</p>;
    }

    const { fields } = formBody;

    // Extract unique sections dynamically from fields
    const sections = Array.from(
      new Set(
        fields
          .filter(
            (field: FieldType) => field.sections && field.sections.length > 0,
          )
          .flatMap((field: FieldType) => field.sections),
      ),
    );

    return (
      <div className="form-preview">
        <div className="form-preview-title">Form Preview</div>

        {/* Render sections dynamically */}
        {sections.length > 0 &&
          sections.map((section) => {
            const sectionFields = fields.filter(
              (f: FieldType) => f.sections && f.sections.includes(section),
            );
            if (sectionFields.length === 0) return null;

            return (
              <div key={section} className="form-section">
                <div className="form-section-title">{section}</div>
                {sectionFields.map((field: FieldType) => (
                  <div key={field.id} className="form-field">
                    <div className="form-field-label">
                      {field.label}
                      <span className="form-field-type">({field.type})</span>
                      {field.required && (
                        <span style={{ color: "red" }}>*</span>
                      )}
                    </div>
                    {field.options && (
                      <div>
                        <strong>Options:</strong> {field.options.join(", ")}
                      </div>
                    )}
                    {field.placeholder && (
                      <div style={{ fontSize: "11px", color: "#999" }}>
                        Placeholder: {field.placeholder}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

        {/* Render fields without sections */}
        {(() => {
          const fieldsWithoutSections = fields.filter(
            (f: FieldType) => !f.sections || f.sections.length === 0,
          );
          if (fieldsWithoutSections.length === 0) return null;

          return (
            <div className="form-section">
              <div className="form-section-title">Other Fields</div>
              {fieldsWithoutSections.map((field: FieldType) => (
                <div key={field.id} className="form-field">
                  <div className="form-field-label">
                    {field.label}
                    <span className="form-field-type">({field.type})</span>
                    {field.required && <span style={{ color: "red" }}>*</span>}
                  </div>
                  {field.options && (
                    <div>
                      <strong>Options:</strong> {field.options.join(", ")}
                    </div>
                  )}
                  {field.placeholder && (
                    <div style={{ fontSize: "11px", color: "#999" }}>
                      Placeholder: {field.placeholder}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Show all fields if no sections exist at all */}
        {sections.length === 0 && (
          <div className="form-section">
            <div className="form-section-title">All Fields</div>
            {fields.map((field: FieldType) => (
              <div key={field.id} className="form-field">
                <div className="form-field-label">
                  {field.label}
                  <span className="form-field-type">({field.type})</span>
                  {field.required && <span style={{ color: "red" }}>*</span>}
                </div>
                {field.options && (
                  <div>
                    <strong>Options:</strong> {field.options.join(", ")}
                  </div>
                )}
                {field.placeholder && (
                  <div style={{ fontSize: "11px", color: "#999" }}>
                    Placeholder: {field.placeholder}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <div className="createCategory">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/form">Form</Link>,
            },
            {
              title: "Show",
            },
          ]}
        />

        <div className="titleHeader">
          <h2 className="title">Show</h2>
          <div className="actionButton">
            <Button
              icon={<BiEdit />}
              size="large"
              onClick={() =>
                navigate(`/form/edit`, {
                  state: { form: state?.form },
                })
              }
            >
              Edit
            </Button>
            <Button
              icon={<BsTrash />}
              size="large"
              onClick={handleDelete}
              loading={isLoading}
            >
              Delete
            </Button>
          </div>
        </div>

        <div className="form">
          <div className="row">
            <p className="label">Title</p>
            <p className="value">{formObj?.title}</p>
          </div>
          <div className="row">
            <p className="label">Description</p>
            <p className="value">{formObj?.description || "No description"}</p>
          </div>
          <div className="row">
            <p className="label">ID</p>
            <p className="value">{formObj?.id}</p>
          </div>
          <div className="row">
            <p className="label">Status</p>
            <Tag
              color={
                formObj?.status === "published"
                  ? "green"
                  : formObj?.status === "draft"
                    ? "orange"
                    : "red"
              }
            >
              {formObj?.status || "draft"}
            </Tag>
          </div>
          <div className="row">
            <p className="label">Version</p>
            <p className="value">{formObj?.version || "1.0"}</p>
          </div>
          <div className="row">
            <p className="label">Package</p>
            <p className="value">
              {formObj?.packageName || formObj?.packageId}
            </p>
          </div>
          <div className="row">
            <p className="label">Category</p>
            <p className="value">
              {formObj?.categoryName || formObj?.categoryId}
            </p>
          </div>
          {formObj?.subCategoryName && (
            <div className="row">
              <p className="label">Sub Category</p>
              <p className="value">
                {formObj?.subCategoryName || formObj?.subCategoryId}
              </p>
            </div>
          )}
          <div className="row">
            <p className="label">Created Date</p>
            <p className="value">
              {formObj?.createdAt
                ? new Date(formObj.createdAt).toLocaleDateString()
                : "N/A"}
            </p>
          </div>
          <div className="row">
            <p className="label">Last Updated</p>
            <p className="value">
              {formObj?.updatedAt
                ? new Date(formObj.updatedAt).toLocaleDateString()
                : "N/A"}
            </p>
          </div>

          {formObj?.formBody && (
            <>
              <Divider />
              {renderFormPreview(formObj.formBody)}
            </>
          )}
        </div>
      </div>
    </>
  );
};
